import { cloneDeep } from "lodash";
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, catchError, map, of, tap } from "rxjs";
import { environment } from "src/environments/environment";
import { IPayload, Payload } from "../interface/filters";
import { Fieldwork } from "../modules/pages/fieldwork-report/models/fieldwork";
import { FieldworkAdapter } from "../modules/pages/fieldwork-report/models/fieldwork-adapter";
import { IApiService } from "../modules/records/models/api/i-api-service";
import { ApplicationService } from "./application.service";
import { MyUtils } from "./my-utils.service";
import { FieldworkReportSummary } from "../widgets/fieldwork-panel-filters/fieldwork-report-status";
import { FollowUp } from "./models/folloup-table-model";
import { genFieldworkTableConfig } from "../modules/pages/fieldwork-report/fieldwork-table-config";
import { IMappedApiCollection } from "../modules/records/models/api/i-mapped-api-collection";
import { TableStructure } from "../modules/records/models/table-structure";
import { TextCol } from "../modules/records/models/columns/text-col";
import { RatingCol } from "../shared/table-col-configs/rating-col";
import { AuthService } from "./auth.service";
import { DownloadLogService } from "./download-log.service";
import { Router } from "@angular/router";
import { FlagUniqueService } from "./flag-unique.service";

@Injectable({
    providedIn: "root",
})
export class FieldworkReportService implements IApiService<Fieldwork> {
    endpoint = environment.csibefrontend;
    isLoading = false;
    fieldowrkReportPath = "";
    isFieldworkReportAsync = true;
    reportStatusValue: string = "";
    private _reports = new BehaviorSubject<Fieldwork[]>([]);

    get item$() {
        return this._reports.asObservable();
    }

    constructor(
        private http: HttpClient,
        private fieldworkAdapter: FieldworkAdapter,
        private utils: MyUtils,
        private applicationService: ApplicationService,
        private authService: AuthService,
        private ds: DownloadLogService,
        private router: Router,
        private flagUniqueService: FlagUniqueService
    ) {
    }

    getAll(
        filter?: any,
        skip?: number,
        limit?: number,
        sortDirection?: string,
        sortBy?: string,
        extraFilters?: {
            searchBy: string;
            searchFor: string;
            DateColumn: string;
            reportStatus: keyof FieldworkReportSummary;
            FlagUnique: string;
        },
        tableConfig?: TableStructure<Fieldwork>
    ) {
        if (this.router.url != null && this.router.url.includes('elastic')) {
            this.fieldowrkReportPath = 'roar-reports/v1/fieldwork/data';
        } else {
            this.fieldowrkReportPath = 'v3/getFieldworkReportList';
        }
        let payload: IPayload = Object.assign({}, filter || {});

        payload.question = {
            limit: "",
            locale: this.applicationService.languageSelectValue,
            orderByScore: "",
            questionCode: "",
            section: this.applicationService.navBarSelectValue.idItem,
            tab: this.applicationService.navBarSelectValue.idItem,
        };

        payload.extraFilters = {
            OrderByColumn: sortBy || "",
            Page: skip ? skip + 1 : 1,
            ItemsPerPage: limit,
            OrderBy: sortDirection || "",
            SearchColumn: extraFilters && extraFilters.searchBy ? extraFilters.searchBy : "",
            SearchFor: extraFilters && extraFilters.searchFor ? extraFilters.searchFor : "",
            ReportStatus: extraFilters?.reportStatus,
            DateColumn: extraFilters?.DateColumn,
            FlagUnique: this.flagUniqueService.flagUnique
        };

        if (payload.extraFilters.FlagUnique === 'false') {
            delete payload.extraFilters.FlagUnique;
        }

        this.reportStatusValue = extraFilters ? extraFilters.reportStatus : "";
        this.isLoading = true;
        return this.http.post(this.endpoint + this.fieldowrkReportPath, payload).pipe(
            map((res) => this.fieldworkAdapter.mapCollection(res)),
            tap((res) => {
                const isCec = this.applicationService.navBarSelectValue.idItem.includes('CEC')
                if (tableConfig && isCec) {
                    this.configCecTable(res, tableConfig)
                }
                this._reports.next(res.items)
            }),
            tap((_) => (this.isLoading = false)),
            catchError((err) => {
                this.isLoading = false;
                return of(this.fieldworkAdapter.mapCollection());
            })
        );
    }

    private configCecTable(
        res: IMappedApiCollection<Fieldwork>,
        tableConfig: TableStructure<Fieldwork>
    ) {
        const interview = res.items[0];
        let dealerNameIndex: number = tableConfig.cols.findIndex(col => col.columnDef === 'dealerName');

        for (let key in interview) {
            let col = tableConfig.cols.find((col) => col.columnDef === key);
            let val = interview[key as keyof Fieldwork];
            if (key[0] === "Q") {
                if (!col) {
                    let q = (val as string).split("#");
                    let newCol = new RatingCol<any>(key);
                    newCol.removable = false;
                    newCol.header = newCol.header ?? key;
                    newCol.isQuestionCol = true;
                    tableConfig.cols.splice(++dealerNameIndex, 0, newCol);
                }
            }
        }
        res.items.forEach((el) => {
            tableConfig.cols
                .filter((col) => col.isQuestionCol)
                .forEach((col) => {
                    let val = el[col.columnDef as keyof Fieldwork];
                    if (val && typeof val === "string") {
                        let q = val.split("#");
                        let questionValue = q[1] ? q[1] : val;
                        let f = col.columnDef as Exclude<keyof Fieldwork, "disabled">;
                        el[f] = questionValue;
                    }
                });
        });
    }

    delete(el: Fieldwork) {
        //do something..
        return of(null);
    }

    print(el: Fieldwork) {
        alert("print");
        return of(null);
    }

    loadFollowUp(filter: Payload) {
        return this.http
            .post<FollowUp[]>(this.endpoint + "v1/getFieldworkFollowup", filter)
            .pipe(catchError((err) => of([])));
    }

    downloadExcel(el: Fieldwork, isElastic: boolean, extraFilters: any) {

        let exportFieldworkPath = ""

        if (this.router.url != null && this.router.url.includes('elastic')) {
            exportFieldworkPath = this.isFieldworkReportAsync ? 'roar-reports/v1/fieldwork/async' : 'v5/exportFieldwork';
        } else {
            exportFieldworkPath = this.isFieldworkReportAsync ? 'v4/exportFieldworkReport' : 'v2/exportFieldworkReport';
        }

        let payload: IPayload = cloneDeep(this.utils.settingsPayloadValue.payload);
        payload.question.section = this.applicationService.navBarSelectValue.idItem;
        payload.question.locale = this.applicationService.languageSelectValue;
        payload.question.tab = 'REPORT'
        payload.extraFilters = {
            SearchColumn: extraFilters && extraFilters.searchBy ? extraFilters.searchBy : "",
            SearchFor: extraFilters && extraFilters.searchFor ? extraFilters.searchFor : "",
            ReportStatus: extraFilters && extraFilters.reportStatus ? extraFilters.reportStatus : "",
            FlagUnique: this.flagUniqueService.flagUnique
        };

        if (payload.extraFilters.FlagUnique === 'false') {
            delete payload.extraFilters.FlagUnique;
        }
        return this.http
            .post(this.endpoint + exportFieldworkPath, payload, {
                responseType: "blob",
            })
            .pipe(
                tap((data) => {
                    if (!this.isFieldworkReportAsync) {
                      
                        const file = new Blob([data], { type: "text/csv;charset=utf-8" });
                        const fileURL = URL.createObjectURL(file);
                        const a = document.createElement("a");
                        a.href = fileURL;
                        a.download = "exportFieldworkReport" + ".xlsx";
                        a.target = "_blank";
                        a.click();
                    } else {
                        this.ds.openSnackBar();
                    }
                })
            );
    }

    downloadExcelRejectedReason() {
        let payload = cloneDeep(this.utils.settingsPayloadValue.payload);
        payload.question.section = this.applicationService.navBarSelectValue.idItem;
        payload.question.locale = this.applicationService.languageSelectValue;

        return this.http
            .post(this.endpoint + `v5/exportFieldworkRejectedReasons`, payload, {
                responseType: "blob",
            })
            .pipe(
                tap((data) => {
                    const file = new Blob([data], { type: "text/csv;charset=utf-8" });
                    const fileURL = URL.createObjectURL(file);
                    const a = document.createElement("a");
                    a.href = fileURL;
                    a.download = "exportRejectedReasons" + ".xlsx";
                    a.target = "_blank";
                    a.click();
                })
            );
    }

    genTableConfig() {

        const isMase = this.authService.meSelectValue?.pathbrand.includes("b_MASE") ?? false;
        const isCec = this.applicationService.navBarSelectValue.idItem.includes('CEC');
        const isSalesSA = this.applicationService.navBarSelectValue.idItem === 'SALES' && this.authService.meSelectValue?.region === 'SA';
        const isSolOrDelivery = this.applicationService.navBarSelectValue.idItem === 'ORDER' || this.applicationService.navBarSelectValue.idItem === 'DELIVERY';
        const isElastic = this.router.url.includes('elastic');
        return genFieldworkTableConfig(this, isCec, isMase, isSalesSA, isSolOrDelivery, isElastic);
    }
}
